import { AppModuleCode, PermissionCode, type TicketListSortBy } from '@gb-ticket/shared-types';
import { ChevronLeftIcon, ChevronRightIcon, Loader2Icon } from 'lucide-react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import { useModule } from '@/features/auth/use-module';
import { TicketsFilters } from '@/products/helpdesk/features/tickets/tickets-filters';
import { TicketsTable } from '@/products/helpdesk/features/tickets/tickets-table';
import { usePersistedTicketListQuery } from '@/products/helpdesk/features/tickets/use-persisted-ticket-list-query';
import { useTicketLookups, useTickets } from '@/products/helpdesk/features/tickets/use-tickets';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';

export function TicketsPage() {
  usePageTitle('Tickets');
  const me = useMe();
  const canCreate = hasPermission(me.data, PermissionCode.TICKETS_CREATE);
  const changesOn = useModule(AppModuleCode.CHANGES);
  const lookups = useTicketLookups();
  const { query, setQuery, patchQuery, searchInput, setSearchInput, ready } =
    usePersistedTicketListQuery('agent', me.data?.id);
  const tickets = useTickets(query, { enabled: ready });

  const totalPages = tickets.data
    ? Math.max(1, Math.ceil(tickets.data.total / tickets.data.pageSize))
    : 1;

  function onSortChange(sortBy: TicketListSortBy) {
    setQuery((current) => ({
      ...current,
      sortBy,
      sortOrder: current.sortBy === sortBy && current.sortOrder === 'asc' ? 'desc' : 'asc',
      page: 1,
    }));
  }

  return (
    <section className="min-w-0 space-y-4">
      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight">Tickets</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Suche in Betreff, Beschreibung und Ticketnummer.
          </p>
        </div>
        {canCreate && (
          <div className="flex flex-wrap gap-2">
            {changesOn && (
              <Button asChild variant="outline">
                <Link to="/helpdesk/changes">Change-Kalender</Link>
              </Button>
            )}
            <Button
              asChild
              className="bg-brand-red text-white hover:bg-brand-red/90 focus-visible:ring-brand-red/30"
            >
              <Link to="/helpdesk/tickets/neu">Neues Ticket</Link>
            </Button>
          </div>
        )}
      </div>

      <TicketsFilters
        query={query}
        lookups={lookups.data}
        searchInput={searchInput}
        onSearchInputChange={setSearchInput}
        onChange={patchQuery}
      />

      {(!ready || tickets.isPending) && (
        <p className="flex items-center gap-2 text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Tickets werden geladen …
        </p>
      )}
      {tickets.isError && (
        <p role="alert" className="text-sm text-destructive">
          {tickets.error instanceof ApiRequestError
            ? tickets.error.message
            : 'Die Ticketliste konnte nicht geladen werden.'}
        </p>
      )}
      {tickets.data && (
        <>
          <div className="min-w-0 overflow-x-auto rounded-md border">
            <TicketsTable items={tickets.data.items} query={query} onSortChange={onSortChange} />
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground" aria-live="polite">
              {tickets.data.total === 0
                ? 'Keine Einträge'
                : `${tickets.data.total} Tickets · Seite ${tickets.data.page} von ${totalPages}`}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={query.page <= 1}
                onClick={() => patchQuery({ page: query.page - 1 })}
              >
                <ChevronLeftIcon />
                Zurück
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={query.page >= totalPages}
                onClick={() => patchQuery({ page: query.page + 1 })}
              >
                Weiter
                <ChevronRightIcon />
              </Button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
